import BaseAuthAdapter from './BaseAuthAdapter';
import { initAzureAuthentication } from '@vezeeta/azure-auth';
import authentication from 'configs/authentication';
import urls from 'configs/urls';

/**
 * Azure AD Authentication Adapter
 * 
 * Handles authentication using Azure AD (standalone mode)
 */
class AzureAuthAdapter extends BaseAuthAdapter {
  constructor() {
    super();
    const { authConfig, redirectUrlCookieName } = authentication;

    // Initialize Azure authentication
    this.azureAuth = initAzureAuthentication({
      authConfig,
      getAuthTokenApi: urls.accountManagementUrls.getApiAuthToken,
      applicationKey: import.meta.env.REACT_APP_AZURE_AD_APP_APPLICATION_KEY,
      redirectUrlCookieName,
    });

    // Cache token after validation for synchronous access
    this.cachedToken = null;
    this.tokenValidated = false;
  }

  /**
   * Validate if the user is authenticated
   */
  async validateUser(forceRefresh = false) {
    try {
      const result = await this.azureAuth.validateUser(forceRefresh);
      const token = result.appCookie || null;
      const isValidUser = result.isValidUser || false;

      // Cache token if validation was successful
      if (isValidUser && token) {
        this.cachedToken = token;
        this.tokenValidated = true;
      } else {
        // Clear cache if validation failed
        this.cachedToken = null;
        this.tokenValidated = false;
      }

      return {
        isValidUser,
        userInfo: result.userInfo || null,
        token,
        isEmbedded: false,
      };
    } catch (error) {
      console.error('Azure AD validation error:', error);
      // Clear cache on error
      this.cachedToken = null;
      this.tokenValidated = false;
      return {
        isValidUser: false,
        userInfo: null,
        token: null,
        isEmbedded: false,
      };
    }
  }

  /**
   * Get the current authentication token
   * Returns cached token if available (from previous validation)
   * Returns null if token hasn't been validated yet
   */
  getToken() {
    // Return cached token if available
    // Note: Token is cached after validateUser() is called
    // If getToken() is called before validation, it will return null
    // Call validateUser() first to populate the cache
    return this.cachedToken;
  }

  /**
   * Initiate login flow
   */
  async login(options = {}) {
    if (!this.azureAuth.loginPopup) {
      throw new Error('Login not available in Azure AD adapter');
    }

    const dateAfterOneWeek = options.expirationDate || new Date(new Date().setHours(168));
    return this.azureAuth.loginPopup(dateAfterOneWeek);
  }

  /**
   * Handle logout
   */
  async logout() {
    if (!this.azureAuth.logoutPopup) {
      throw new Error('Logout not available in Azure AD adapter');
    }

    const result = await this.azureAuth.logoutPopup();
    
    // Clear cached token on logout
    this.cachedToken = null;
    this.tokenValidated = false;
    
    return result;
  }

  /**
   * Get user information
   */
  async getUserInfo() {
    const result = await this.validateUser();
    return result.userInfo;
  }

  /**
   * Check if user is authenticated (synchronous)
   * Returns true if token has been validated and cached
   * Note: This is a best-effort check based on cached state
   * Use validateUser() for accurate async validation
   */
  isAuthenticated() {
    // Return true if we have a cached token from previous validation
    return this.tokenValidated && !!this.cachedToken;
  }

  /**
   * Refresh the authentication token
   */
  async refreshToken() {
    const result = await this.validateUser(true); // Force refresh
    return result.token;
  }
}

export default AzureAuthAdapter;

